import { deleteRest, get, patch, post, postForm, put } from 'utils/rxfetch'
import { buildURL } from 'utils/urls'

import { WHITELIST_TABLE_DEFAULT_PAGE_SIZE } from 'containers/Settings/Whitelist/constants'

import { getEmailEncryptionBranding, updateEmailEncryptionBranding } from './branding'

const DMARC_REQUEST_INFO_ENDPOINT = '/members-api/settings/dmarc-request-info'
const ENCRYPTION_PLG_ENDPOINT = '/members-api/settings/encryption-demo-request'
const DEEPFAKE_ONBOARDING_WIZARD_CONFIG_ENDPOINT = '/members-api/deepfake/onboarding-wizard-config/'
const DEEPFAKE_KEY_PERSON_AD_GROUPS_ENDPOINT = '/members-api/deepfake/company/key-person-ad-groups/'
const DEEPFAKE_MEETINGS_ENDPOINT = '/members-api/deepfake/company/meetings/'
const DEEPFAKE_FALSE_POSITIVE_ENDPOINT = '/members-api/deepfake/company/false-positive/'

export default () => ({
  // Profile
  getProfileSettings() {
    return get('/members-api/settings/profile')
  },
  updateProfileSettings(settings) {
    return put('/members-api/settings/profile', settings)
  },
  updPassword(passwords) {
    return put('/members-api/account/password', passwords)
  },
  getPushNotificationSettings() {
    return get('/members-api/settings/push-notifications')
  },
  updatePushNotificationSettings(fields) {
    return put('/members-api/settings/push-notifications', fields)
  },
  // General
  getGeneralSettings(companyId = '') {
    return get(`/members-api/settings/general?companyFilter=${companyId}`)
  },
  updateGeneralSettings(companyId = '', settings) {
    return put(`/members-api/settings/general?companyFilter=${companyId}`, settings)
  },
  delCompanyLogo() {
    return deleteRest('/members-api/account/company-logo')
  },
  // Users
  getProfiles(companyId = '', filter = 'all') {
    return get(`/members-api/profiles/${filter}?companyFilter=${companyId}`)
  },
  getCompanyUsers(companyId = '', sortProperty, assignedCompanies) {
    return get(
      buildURL(
        '/members-api/account/users/',
        {
          companyFilter: companyId,
          sortProperty,
          assignedCompanies,
        },
        true
      )
    )
  },
  getManageableCompanies({ companyId, page, pageSize, search }) {
    return get(
      buildURL('/members-api/partner/manageable-companies', {
        companyFilter: companyId,
        page,
        pageSize,
        search,
      })
    )
  },
  getUserInfo(companyId, id) {
    return get(
      buildURL(`/members-api/account/users/${id}/`, {
        companyFilter: companyId,
      })
    )
  },
  delCompanyUser(companyId = '', userId) {
    return deleteRest(`/members-api/account/users/${userId}/?companyFilter=${companyId}`)
  },
  updCompanyUser(companyId = '', user) {
    return put(`/members-api/account/users/${user.id}/?companyFilter=${companyId}`, user)
  },
  addCompanyUser(companyId = '', user) {
    return post(`/members-api/account/users/?companyFilter=${companyId}`, user)
  },
  makeOwner(companyId = '', user) {
    return put(`/members-api/settings/makeowner?companyFilter=${companyId}`, user)
  },
  getManageableUserCompanies({
    id,
    companyId = '',
    page,
    pageSize,
    company,
    planTypes,
    mspRoleIds,
    isAssigned,
    allowedMspRoleId,
  }) {
    return get(
      buildURL(
        `/members-api/account/users/${id}/manageable-companies/`,
        {
          companyFilter: companyId,
          page,
          pageSize,
          company,
          planTypes,
          mspRoleIds,
          isAssigned,
          allowedMspRoleId,
        },
        true
      )
    )
  },
  assignCompanies({ id, companyId = '', data }) {
    return post(
      `/members-api/account/users/${id}/manageable-companies/?companyFilter=${companyId}`,
      data
    )
  },
  unassignMSPCompanies({ id, companyId = '', data }) {
    return deleteRest(
      `/members-api/account/users/${id}/manageable-companies/?companyFilter=${companyId}`,
      data,
      { 'Content-Type': 'application/json' }
    )
  },
  // Whitelist
  addWhitelist(companyId = '', item) {
    return post(`/members-api/settings/allowlist?companyFilter=${companyId}`, item)
  },

  editWhitelist(companyId = '', item) {
    return put(`/members-api/settings/allowlist?companyFilter=${companyId}`, item)
  },
  getWhitelisted(
    companyId = '',
    typeFilter = '',
    sortProperty = '',
    search = '',
    page = 1,
    itemsPerPage = WHITELIST_TABLE_DEFAULT_PAGE_SIZE
  ) {
    return get(
      `/members-api/settings/allowlist?companyFilter=${companyId}&typeFilter=${typeFilter}&sortProperty=${sortProperty}&search=${search}&page=${page}&itemsPerPage=${itemsPerPage}`
    )
  },
  deleteSelectedWhitelists(companyId = '', selectedIds) {
    return deleteRest(`/members-api/settings/allowlist?companyFilter=${companyId}`, { selectedIds })
  },
  // Languages
  getLanguages(companyId = '', associate = false) {
    return get(`/members-api/settings/languages?companyFilter=${companyId}&associate=${associate}`)
  },
  updateLanguages(companyId = '', body) {
    return put(`/members-api/settings/languages?companyFilter=${companyId}`, body)
  },
  // Partner
  getPartnerSettings() {
    return get('/members-api/settings/partner')
  },
  updatePartnerSettings(body) {
    return put('/members-api/settings/partner', body)
  },
  // Incident Response
  getIncidenceResponseSettings(companyId = '') {
    return get(`/members-api/settings/incident?companyFilter=${companyId}`)
  },
  updIncidenceResponseSettings(companyId = '', settings) {
    return put(`/members-api/settings/incident?companyFilter=${companyId}`, settings)
  },
  // IRONSCALES Community
  getFederationSettings(companyId = '') {
    return get(`/members-api/settings/federation?companyFilter=${companyId}`)
  },
  updFederationSettings(companyId = '', settings) {
    return put(`/members-api/settings/federation?companyFilter=${companyId}`, settings)
  },
  getSpamAllowList(companyId = '') {
    return get(`/members-api/spam-enabling/senders-counter?companyFilter=${companyId}`)
  },
  createSpamAllowList(emailsList, companyId = '') {
    return post(`/members-api/spam-enabling/bulk-create-whitelist?companyFilter=${companyId}`, {
      sender_emails: emailsList,
    })
  },
  toggleSpamFF(ffState, companyId = '') {
    return post(`/members-api/spam-enabling/toggle-ff?companyFilter=${companyId}`, {
      'ff-state': ffState,
    })
  },
  // Mailbox Protection
  getBanners(companyId = '') {
    return get(`/members-api/settings/ironsights?companyFilter=${companyId}`)
  },
  updBanners(companyId = '', values) {
    return put(`/members-api/settings/ironsights?companyFilter=${companyId}`, values)
  },
  getSightsLandingPages(companyId = '') {
    return get(`/members-api/ironsights/landings?companyFilter=${companyId}`)
  },
  updateSightsLandingPages(companyId = '', values) {
    return put(`/members-api/ironsights/landings?companyFilter=${companyId}`, values)
  },
  // Irontraps Customization
  getCustomizationSettings(companyId = '', shortName = 'en') {
    return get(`/members-api/settings/customization/${shortName}?companyFilter=${companyId}`)
  },
  updateCustomizationSettings(companyId = '', settings) {
    return put(`/members-api/settings/customization?companyFilter=${companyId}`, settings)
  },
  // Irontraps Customization O365
  getNotifications(companyId = '', locale = '') {
    return get(`/owa/notifications/?companyFilter=${companyId}&locale=${locale}`)
  },
  updateNotifications(companyId = '', body, locale = '') {
    return postForm(`/owa/notifications/?companyFilter=${companyId}&locale=${locale}`, body)
  },
  restoreNotifications(companyId = '', locale = '') {
    return deleteRest(`/owa/notifications/?companyFilter=${companyId}&locale=${locale}`)
  },
  getReportingReasons(companyId = '', locale = '') {
    return get(
      `/members-api/irontraps/reports/comment/options/?companyFilter=${companyId}&locale=${locale}`
    )
  },
  updateReportingReasons(companyId = '', body, locale = '') {
    return put(
      `/members-api/irontraps/reports/comment/options/?companyFilter=${companyId}&locale=${locale}`,
      body
    )
  },
  restoreDefaultReasons(companyId = '', locale = '') {
    return deleteRest(
      `/members-api/irontraps/reports/comment/options/?companyFilter=${companyId}&locale=${locale}`
    )
  },
  // Simulator
  getSimulatorSettings(companyId = '') {
    return get(`/members-api/settings/simulator?companyFilter=${companyId}`)
  },
  updateSimulatorSettings(companyId = '', settings) {
    return put(`/members-api/settings/simulator?companyFilter=${companyId}`, settings)
  },
  getCandidates(companyId = '', page, pageSize, search) {
    return get(
      buildURL(`/members-api/campaign-report-recipients/candidates/?companyFilter=${companyId}`, {
        page,
        pageSize,
        search,
      })
    )
  },
  getRecipients(companyId = '', page, pageSize) {
    return get(
      buildURL(`/members-api/campaign-report-recipients/?companyFilter=${companyId}`, {
        page,
        pageSize,
      })
    )
  },
  addRecipient(companyId = '', recipients, tags) {
    return post(`/members-api/campaign-report-recipients/?companyFilter=${companyId}`, {
      recipients,
      tags,
    })
  },
  removeRecipients(companyId = '', selectionData) {
    return deleteRest(
      `/members-api/campaign-report-recipients/delete-bulk/?companyFilter=${companyId}`,
      {
        selectionData,
      },
      { 'Content-Type': 'application/json' }
    )
  },
  // IP Range
  getIpRangeList(companyId = '', search, withinLastDays) {
    return get(
      buildURL(`/members-api/settings/ignore-ips/?companyFilter=${companyId}`, {
        search,
        withinLastDays,
      })
    )
  },
  createIpRange(companyId = '', settings) {
    return post(`/members-api/settings/ignore-ips/?companyFilter=${companyId}`, settings)
  },
  updateIpRange(companyId = '', id, settings) {
    return put(`/members-api/settings/ignore-ips/${id}/?companyFilter=${companyId}`, settings)
  },
  deleteIpRange(companyId = '', ids) {
    return deleteRest(
      `/members-api/settings/ignore-ips/delete-bulk/?companyFilter=${companyId}`,
      {
        ids,
      },
      {
        'Content-Type': 'application/json',
      }
    )
  },
  // Email Server
  getEmailServerSettings(companyId = '') {
    return get(`/members-api/settings/emailserver?companyFilter=${companyId}`)
  },
  updEmailServerSettings(companyId = '', settings) {
    return put(`/members-api/settings/emailserver?companyFilter=${companyId}`, settings)
  },
  delExchangeCred(companyId = '') {
    return deleteRest(`/members-api/settings/emailserver?companyFilter=${companyId}`)
  },
  updMailboxInfo(companyId = '', mailbox) {
    return post(`/members-api/settings/emailserver?companyFilter=${companyId}`, {
      new_mailbox: mailbox,
    })
  },
  disable911Mailbox(companyId = '') {
    return put(`/members-api/settings/disable911?companyFilter=${companyId}`)
  },
  saveQuickIntegration(companyId = '', body) {
    return post(`/members-api/integrations/quick-deployment?companyFilter=${companyId}`, body)
  },
  getGsuiteStatus(companyId = '') {
    return get(`/members-api/integrations/gmail-webhooks?companyFilter=${companyId}`)
  },
  enableGsuiteIntegration(companyId = '') {
    return post(`/members-api/integrations/gmail-webhooks?companyFilter=${companyId}`)
  },
  disableGsuiteIntegration(companyId = '') {
    return deleteRest(`/members-api/integrations/gmail-webhooks?companyFilter=${companyId}`)
  },
  deleteGsuiteCredentials(companyId = '') {
    return deleteRest(
      `/members-api/integrations/gmail-webhooks/credentials?companyFilter=${companyId}`
    )
  },
  getO365Status(companyId = '') {
    return get(`/members-api/integrations/office365-webhooks?companyFilter=${companyId}`)
  },
  getPowershellStatus(companyId = '') {
    return get(
      `/members-api/office365/integrations/office365-powershell?companyFilter=${companyId}`
    )
  },
  disableO365Integration(companyId = '') {
    return deleteRest(`/members-api/integrations/office365-webhooks?companyFilter=${companyId}`)
  },
  disablePowershellIntegration(companyId = '') {
    return deleteRest(
      `/members-api/office365/integrations/office365-powershell?companyFilter=${companyId}`
    )
  },
  // Malware
  getMalwareDetection(companyId = '', name) {
    return get(`/members-api/integrations/${name}?companyFilter=${companyId}`)
  },
  updateMalwareDetection(companyId = '', name, body) {
    return post(`/members-api/integrations/${name}?companyFilter=${companyId}`, body)
  },
  deleteMalwareDetection(companyId = '', name) {
    return deleteRest(`/members-api/integrations/${name}?companyFilter=${companyId}`)
  },
  deleteIntegration(companyId = '', name) {
    return deleteRest(`/members-api/integrations/${name}?companyFilter=${companyId}`)
  },
  // getVxStreamsEnvs(companyId = '', api_key, secret_key, url) {
  //   return post(`/members-api/integrations/vxstream/envs?companyFilter=${companyId}`, { api_key, secret_key, url },)
  // },
  getAlertEventTypes(dataModelVersion) {
    const versionParam = dataModelVersion ? `?dataModelVersion=${dataModelVersion}` : ''
    return get(`/members-api/settings/alerts/event-types${versionParam}`)
  },
  getIncidentFilters() {
    return get('/members-api/settings/alerts/incident-filters')
  },
  listEventRules(companyId = '') {
    return get(`/members-api/settings/alerts/event-rules/?companyFilter=${companyId}`)
  },
  createEventRule(companyId = '', rule) {
    return post(`/members-api/settings/alerts/event-rules/?companyFilter=${companyId}`, rule)
  },
  updateEventRule(companyId = '', ruleId, rule) {
    return put(
      `/members-api/settings/alerts/event-rules/${ruleId}/?companyFilter=${companyId}`,
      rule
    )
  },
  deleteEventRule(companyId = '', ruleId) {
    return deleteRest(
      `/members-api/settings/alerts/event-rules/${ruleId}/?companyFilter=${companyId}`
    )
  },
  enableEventRule(companyId = '', ruleId) {
    return post(
      `/members-api/settings/alerts/event-rules/${ruleId}/enable/?companyFilter=${companyId}`,
      {}
    )
  },
  disableEventRule(companyId = '', ruleId) {
    return post(
      `/members-api/settings/alerts/event-rules/${ruleId}/disable/?companyFilter=${companyId}`,
      {}
    )
  },
  getEventRuleScopeAuthority(companyId = '') {
    return get(
      `/members-api/settings/alerts/event-rules/scope-authority/?companyFilter=${companyId}`
    )
  },
  getEventRule(companyId = '', ruleId) {
    return get(`/members-api/settings/alerts/event-rules/${ruleId}/?companyFilter=${companyId}`)
  },
  setEventRuleScopeMode(companyId = '', ruleId, isAllCompanies) {
    return put(
      `/members-api/settings/alerts/event-rules/${ruleId}/scope/mode/?companyFilter=${companyId}`,
      { isAllCompanies }
    )
  },
  setEventRuleScope(companyId = '', ruleId, operation, companyIds) {
    return put(
      `/members-api/settings/alerts/event-rules/${ruleId}/scope/?companyFilter=${companyId}`,
      { operation, companyIds }
    )
  },
  cloneEventRule(companyId = '', ruleId) {
    return post(
      `/members-api/settings/alerts/event-rules/${ruleId}/clone/?companyFilter=${companyId}`,
      {}
    )
  },
  listDestinations(companyId = '', dataModelVersion) {
    const dataModelVersionParam = dataModelVersion ? `&dataModelVersion=${dataModelVersion}` : ''
    return get(
      `/members-api/settings/alerts/destinations/?companyFilter=${companyId}${dataModelVersionParam}`
    )
  },
  createDestination(companyId = '', destination) {
    return post(
      `/members-api/settings/alerts/destinations/?companyFilter=${companyId}`,
      destination
    )
  },
  getDestination(companyId = '', destinationId) {
    return get(
      `/members-api/settings/alerts/destinations/${destinationId}/?companyFilter=${companyId}`
    )
  },
  updateDestination(companyId = '', destinationId, destination) {
    return put(
      `/members-api/settings/alerts/destinations/${destinationId}/?companyFilter=${companyId}`,
      destination
    )
  },
  deleteDestination(companyId = '', destinationId) {
    return deleteRest(
      `/members-api/settings/alerts/destinations/${destinationId}/?companyFilter=${companyId}`
    )
  },
  sendSampleDestination(companyId = '', destination) {
    return post(
      `/members-api/settings/alerts/destinations/send-sample/?companyFilter=${companyId}`,
      destination
    )
  },
  testConnectionDestination(companyId = '', destination) {
    return post(
      `/members-api/settings/alerts/destinations/test-connection/?companyFilter=${companyId}`,
      destination
    )
  },
  // Email Notifications
  getAnnouncements(companyId = '', id) {
    return get(`/members-api/announcements/${parseInt(id)}?companyFilter=${companyId}`)
  },
  getAnnouncementLocale(companyId = '', activeId, localeId, announcementTrigger, isGroupSettings) {
    if (isGroupSettings) {
      return get(
        `/members-api/announcementlocale/group-settings-wizard/${activeId}/${localeId}/${announcementTrigger}?companyFilter=${companyId}`
      )
    } else {
      return get(
        `/members-api/announcementlocale/${activeId}/${localeId}/${announcementTrigger}?companyFilter=${companyId}`
      )
    }
  },
  updateAnnouncementLocale(companyId = '', data) {
    const tag = data.tag == 0 ? '' : data.tag
    return put(
      `/members-api/announcementlocale/${data.announcement_id}/${data.locale_id}/${data.trigger}?companyFilter=${companyId}`,
      {
        text: data.text,
        subject: data.subject,
        template: data.template,
        emailFrequency: data.emailFrequency,
        spamFrequency: data.spamFrequency,
        contactEmail: data.contactEmail,
        dateFilter: data.dateFilter,
        approachingLimitPercentage: data.approachingLimitPercentage,
        microsoftUnifiedReport: data.microsoftUnifiedReport,
        groupBy: data.groupBy,
        tag,
        includeSpam: data.includeSpam,
        askToRelease: data.askToRelease,
        participants: data.localRecipientsData.selected,
      }
    )
  },
  deleteAnnouncementLocale(companyId = '', data) {
    return deleteRest(
      `/members-api/announcementlocale/${data.announcement_id}/${data.locale_id}/${data.trigger}?companyFilter=${companyId}`,
      {}
    )
  },
  getAnnouncementsLogo(companyId = '') {
    return get(`/members-api/announcements/logo?companyFilter=${companyId}`)
  },
  updateAnnouncementsLogo(companyId = '', body) {
    return post(`/members-api/announcements/logo?companyFilter=${companyId}`, body)
  },
  fetchAnnouncementsParticipants(companyId = '', id, data) {
    return get(
      buildURL(`/members-api/announcements/participants/${parseInt(id)}`, {
        companyFilter: companyId,
        searchFilter: data.searchFilter,
      })
    )
  },
  fetchAnnouncementsRecipientsCount(companyId = '', body) {
    return post(`/members-api/campaign/select-participants?companyFilter=${companyId}`, body)
  },
  getSystemNotificationsSettings(companyId = '') {
    return get(`/members-api/settings/system-notifications?companyFilter=${companyId}`)
  },
  updateSystemNotificationsSettings(companyId = '', settings) {
    return put(`/members-api/settings/system-notifications?companyFilter=${companyId}`, settings)
  },
  getCollaborationAppsData(companyId = '', integrationApp) {
    return get(
      `/members-api/integrations/collaboration-apps/${integrationApp}?companyFilter=${companyId}`
    )
  },
  updateCollaborationAppsData(companyId = '', integrationApp, body) {
    return post(
      `/members-api/integrations/collaboration-apps/${integrationApp}?companyFilter=${companyId}`,
      body
    )
  },
  updateCollaborationAppsAlerts(companyId = '', integrationApp, body) {
    return put(
      `/members-api/integrations/collaboration-apps/${integrationApp}?companyFilter=${companyId}`,
      body
    )
  },
  cancelCollaborationAppsIntegration(companyId = '', integrationApp) {
    return deleteRest(
      `/members-api/integrations/collaboration-apps/${integrationApp}?companyFilter=${companyId}`
    )
  },
  // Disclaimers
  getDisclaimers() {
    return get('/members-api/settings/disclaimers')
  },
  addDisclaimer(disclaimer) {
    return post('/members-api/settings/disclaimers', disclaimer)
  },
  updateDisclaimer(disclaimer) {
    return put('/members-api/settings/disclaimers', disclaimer)
  },
  verifyDomain(mailerDomain, companyFilter) {
    return post(
      `/members-api/settings/verify-mailer-domain?${
        companyFilter ? `companyFilter=${companyFilter}` : ''
      }`,
      { mailerDomain }
    )
  },
  getCncAccess(companyFilter) {
    return get(
      `/members-api/account/cnc-access?${companyFilter ? `companyFilter=${companyFilter}` : ''}`
    )
  },
  setCncAccess(cncAccess, companyFilter) {
    return put(
      `/members-api/account/cnc-access?${companyFilter ? `companyFilter=${companyFilter}` : ''}`,
      { cncAccess }
    )
  },
  // Group Settings
  getGroupSettingsName(companyFilter = '') {
    return get(
      `/members-api/settings/group-settings-name?companyFilter=${companyFilter}&include=managedFeatures`
    )
  },
  getMicrosoftQuarantine(companyFilter = '') {
    return get(`/members-api/settings/microsoft?companyFilter=${companyFilter}`)
  },
  updateMicrosoftQuarantine(companyFilter = '', microsoftDetectionsEnabled, powershellEnabled) {
    return put(`/members-api/settings/microsoft?companyFilter=${companyFilter}`, {
      microsoftDetectionsEnabled,
      microsoftReleaseQuarantinedEmailEnabled: powershellEnabled,
    })
  },
  releaseMSQuarantineEmail(messageId, newEid, recipientAddress, index) {
    return post('/members-api/search/investigation/release_email', {
      message_id: messageId,
      new_eid: newEid,
      recipient_address: recipientAddress,
      index,
    })
  },
  releaseMSQuarantineEmailStatus(taskId) {
    return get(
      buildURL('/members-api/search/investigation/release_email', {
        task_id: taskId,
      })
    )
  },
  // DeepFake
  getDeepfakeConfig() {
    return get('/members-api/deepfake/company/')
  },
  updateDeepfakeConfig(body) {
    return put('/members-api/deepfake/company/', body)
  },
  getDeepfakeKeyMembers(params = {}) {
    return get(buildURL('/members-api/deepfake/company/key_members/', params, true))
  },
  getDeepfakeAvailableUsers(params = {}) {
    return get(buildURL('/members-api/deepfake/company/key_members/available-users', params, true))
  },
  getDeepfakeAdGroups(params = {}) {
    return get(buildURL('/members-api/deepfake/company/ad-groups/', params, true))
  },
  getDeepfakeAdGroupsUserCount(body) {
    return post('/members-api/deepfake/company/ad-groups/user-count/', body)
  },
  getDeepfakeKeyPersonAdGroups() {
    return get(DEEPFAKE_KEY_PERSON_AD_GROUPS_ENDPOINT)
  },
  createDeepfakeKeyPersonAdGroups(body) {
    return post(DEEPFAKE_KEY_PERSON_AD_GROUPS_ENDPOINT, body)
  },
  deleteDeepfakeKeyPersonAdGroup(id) {
    return deleteRest(`${DEEPFAKE_KEY_PERSON_AD_GROUPS_ENDPOINT}${id}/`)
  },
  createDeepfakeKeyMember(body) {
    return post('/members-api/deepfake/company/key_members/', body)
  },
  createDeepfakeKeyMembersBulk(body) {
    return post('/members-api/deepfake/company/key_members/bulk-create/', body)
  },
  updateDeepfakeKeyMember(id, body) {
    return put(`/members-api/deepfake/company/key_members/${id}/`, body)
  },
  deleteDeepfakeKeyMember(id) {
    return deleteRest(`/members-api/deepfake/company/key_members/${id}/`)
  },
  getWarningReportRecipients() {
    return get('/members-api/deepfake/warning_report_recipients/')
  },
  getWarningReportCandidates() {
    return get('/members-api/deepfake/warning_report_candidates/')
  },
  addWarningReportRecipients(body) {
    return post('/members-api/deepfake/warning_report_recipients/batch-add/', body)
  },
  deleteWarningReportRecipients(body) {
    return post('/members-api/deepfake/warning_report_recipients/batch-delete/', body)
  },
  getDeepFakeAllowList() {
    return get('/members-api/deepfake/company/whitelist/')
  },
  addDeepFakeAllowList(body) {
    return post('/members-api/deepfake/company/whitelist/', body)
  },
  deleteDeepFakeAllowList(id) {
    return deleteRest(`/members-api/deepfake/company/whitelist/${id}/`)
  },
  getDeepfakeMeetings(params = {}) {
    return get(buildURL(DEEPFAKE_MEETINGS_ENDPOINT, params, true))
  },
  getDeepfakeCalls(params = {}) {
    return get(buildURL(DEEPFAKE_MEETINGS_ENDPOINT, params, true))
  },
  markDeepfakeFalsePositive(callParticipantId, action, reasonText = null) {
    return post(DEEPFAKE_FALSE_POSITIVE_ENDPOINT, { callParticipantId, action, reasonText })
  },
  // Deepfake Wizard
  getDeepfakeWizardConfig() {
    return get(DEEPFAKE_ONBOARDING_WIZARD_CONFIG_ENDPOINT)
  },
  updateDeepfakeWizardConfig(data) {
    return put(DEEPFAKE_ONBOARDING_WIZARD_CONFIG_ENDPOINT, data)
  },
  verifyConsent() {
    return post('/members-api/deepfake/verify-consent/', {})
  },

  // Dmarc request info
  getDmarcRequestInfo() {
    return get(DMARC_REQUEST_INFO_ENDPOINT)
  },
  postDmarcRequestInfo(body) {
    return post(DMARC_REQUEST_INFO_ENDPOINT, body)
  },

  // Encryption PLG demo request
  getEncryptionPLGInfo() {
    return get(ENCRYPTION_PLG_ENDPOINT)
  },
  postEncryptionDemoRequest(body) {
    return post(ENCRYPTION_PLG_ENDPOINT, body)
  },

  // Email Encryption Integration (OES Management Proxy)

  createEmailEncryptionConfig(companyFilter = '') {
    return post(`/members-api/oes-management-proxy/config/?companyFilter=${companyFilter}`, {
      status: 'TESTING',
    })
  },

  updateEmailEncryptionConfig(companyFilter = '', payload) {
    return patch(
      `/members-api/oes-management-proxy/config/?companyFilter=${companyFilter}`,
      payload
    )
  },

  getEmailEncryptionConfig(companyFilter = '') {
    return get(`/members-api/oes-management-proxy/config/?companyFilter=${companyFilter}`)
  },

  getEmailEncryptionConnectionStatus(companyFilter = '') {
    return get(
      `/members-api/oes-management-proxy/connection-status/?companyFilter=${companyFilter}`
    )
  },

  activateEmailEncryptionConfig(companyFilter = '') {
    return patch(
      `/members-api/oes-management-proxy/config/activate/?companyFilter=${companyFilter}`,
      {}
    )
  },

  getEmailEncryptionPolicyCategories(companyFilter = '') {
    return get(
      `/members-api/oes-management-proxy/policy-categories/?companyFilter=${companyFilter}`
    )
  },

  getEmailEncryptionSettings(companyFilter = '') {
    return get(`/members-api/oes-management-proxy/encryption-level/?companyFilter=${companyFilter}`)
  },

  updateEmailEncryptionSettings(settings, companyFilter = '') {
    return put(
      `/members-api/oes-management-proxy/encryption-level/?companyFilter=${companyFilter}`,
      settings
    )
  },

  getEmailEncryptionBranding,
  updateEmailEncryptionBranding,

  getEmailEncryptionKillswitch(companyFilter = '') {
    return get(`/members-api/oes-management-proxy/killswitch/?companyFilter=${companyFilter}`)
  },

  updateEmailEncryptionKillswitch(companyFilter = '', action) {
    return post(`/members-api/oes-management-proxy/killswitch/?companyFilter=${companyFilter}`, {
      action,
    })
  },
})
